import React, { memo, useCallback, useMemo } from 'react';
import { Text } from '@Sage/hoi-poi-ui';
import { getProductBaseUnitDisplayLabel, getProductBaseUnitSymbol } from '@Sage/ssm-utils';
import { useDispatch, useSelector } from 'react-redux';

import { PRODUCTS } from 'constants/Entities';
import { EntityFiltersActions } from 'actions';

import { getLiteral } from 'utils/getLiteral';
import { isUnsetProductQuantity } from 'models/ProductModel';
import { formatDecimalByConfig, DECIMAL_TYPES } from 'utils/decimal';
import { getBackendBoolean } from '@Sage/ssm-utils/backendScalars';
import { getLocalDate } from 'utils/dates';
import MetadataPopover from 'components/MetadataPopover';

import ProductInfoRow from './ProductInfoRow';

import './styles.scss';

const ProductInfo = memo(({ data }) => {
    const schema = useSelector((state) => state?.config?.standardFieldsSchemaMap?.PRODUCTO);
    const environments = useSelector((state) => state?.serverList?.tblSucursales?.data);
    const canShowUom = useSelector((state) => !!state?.config?.userData?.uomEnabled);
    const salesOrderShippingCost = useSelector(
        (state) => state?.config?.userData?.salesOrderShippingCost,
    );
    const useTaxesSalesOrders = useSelector(
        (state) => state?.config?.userData?.useTaxesSalesOrders,
    );
    const dispatch = useDispatch();

    const changeCategory = useCallback(() => {
        dispatch(
            EntityFiltersActions.changeFilter({
                entity: PRODUCTS,
                filter: {
                    id: 'productCategories',
                },
                value: [{ label: data?.ProductCategory, value: data?.ProductCategoryId }],
            }),
        );
    }, [data?.ProductCategory, data?.ProductCategoryId, dispatch]);

    const changeFamily = useCallback(() => {
        if (!data?.ProductFamilyId) return;
        dispatch(
            EntityFiltersActions.changeFilter({
                entity: PRODUCTS,
                filter: {
                    id: 'idProductFamily',
                    noCacheCheck: true,
                    hideForCount: true,
                    byParameter: true,
                    invisible: true,
                },
                value: String(data?.ProductFamilyId),
            }),
        );
    }, [data?.ProductFamilyId, dispatch]);

    const metadata = useMemo(() => {
        return {
            id: data.Id,
            extId: data.extId,
            audit: {
                ...data.Audit,
                readOnlyDb: data.Audit?.readOnlyDb,
            },
            importDate: data.importDate,
            level: data.level,
        };
    }, [data]);

    const environmentsText = useMemo(() => {
        if (!data?.IdEnvironments || !environments?.length) return null;
        const envIds = String(data.IdEnvironments)
            .split(/[,;]/)
            .filter(Boolean)
            .map((id) => parseInt(id, 10));
        const envNames = environments
            .filter((env) => envIds.includes(parseInt(env.value, 10)))
            .map((env) => env.label);
        return envNames.join(', ');
    }, [data?.IdEnvironments, environments]);

    const productEnvironments = useMemo(() => {
        if (!data?.IdEnvironments) return undefined;
        return String(data.IdEnvironments).split(/[,;]/).filter(Boolean);
    }, [data?.IdEnvironments]);

    const baseUnitLabel = useMemo(() => getProductBaseUnitDisplayLabel(data), [data]);

    const baseUnitSymbol = useMemo(() => getProductBaseUnitSymbol(data), [data]);

    const stockValue = useMemo(() => {
        if (isUnsetProductQuantity(data?.Stock)) return '-';
        const formattedStock = formatDecimalByConfig({
            value: data?.Stock,
            type: DECIMAL_TYPES.QUANTITY,
            idEnvironment: productEnvironments,
            entityName: 'PRODUCTS',
            fieldId: 'stock',
        });
        return baseUnitSymbol ? `${formattedStock} ${baseUnitSymbol}` : formattedStock;
    }, [baseUnitSymbol, data?.Stock, productEnvironments]);

    return (
        <div className="fm-products-detail__info">
            <div className="fm-products-detail__info-header">
                <Text type="h6" className="fm-products-detail__title" medium>
                    {getLiteral('label_internal_information')}
                </Text>
                <MetadataPopover data={metadata} entity={PRODUCTS} />
            </div>

            <div className="fm-products-detail__info-rows">
                <ProductInfoRow
                    label="label_price"
                    value={formatDecimalByConfig({
                        value: data?.ProductPrice || 0,
                        type: DECIMAL_TYPES.CURRENCY_PRICES,
                        idEnvironment: productEnvironments,
                        idCurrency: data?.IdCurrency !== '-1' ? data?.IdCurrency : undefined,
                        entityName: 'PRODUCTS',
                        fieldId: 'precio',
                    })}
                    hide={schema?.precio?.isHidden}
                />
                <ProductInfoRow
                    label="label_products_default_tax"
                    value={data?.defaultTaxRate}
                    hide={!useTaxesSalesOrders || schema?.defaultTaxRateId?.isHidden}
                />
                <ProductInfoRow
                    label="label_cost"
                    value={formatDecimalByConfig({
                        value: data?.ProductCost || 0,
                        type: DECIMAL_TYPES.CURRENCY_PRICES,
                        idEnvironment: productEnvironments,
                        idCurrency: data?.IdCurrency !== '-1' ? data?.IdCurrency : undefined,
                        entityName: 'PRODUCTS',
                        fieldId: 'preciocoste',
                    })}
                    hide={schema?.preciocoste?.isHidden}
                />
                <ProductInfoRow
                    label="label_stock"
                    value={stockValue}
                    hide={schema?.stock?.isHidden}
                />
                <ProductInfoRow
                    label="label_unit_of_measure"
                    value={baseUnitLabel}
                    hide={!canShowUom}
                />
                <ProductInfoRow
                    label="label_maxdiscount"
                    value={`${formatDecimalByConfig({
                        value: data?.ProductMaxDiscount
                            ? parseInt(data?.ProductMaxDiscount || 0, 10) * 10
                            : 0,
                        type: DECIMAL_TYPES.GENERAL,
                        idEnvironment: productEnvironments,
                        entityName: 'PRODUCTS',
                        fieldId: 'descmax',
                    })} %`}
                    hide={schema?.descmax?.isHidden}
                />
                <ProductInfoRow
                    label="label_product_type"
                    value={data?.productTypeName}
                    hide={
                        !salesOrderShippingCost ||
                        !schema?.productTypeId ||
                        schema?.productTypeId?.isHidden
                    }
                />
                <ProductInfoRow
                    label="label_category"
                    value={data?.ProductCategory}
                    onClick={changeCategory}
                    hide={schema?.idtipo?.isHidden}
                />
                <ProductInfoRow
                    label="label_product_family"
                    value={
                        data?.ProductFamilyId === '-1'
                            ? getLiteral('title_products')
                            : data?.ProductFamily
                    }
                    onClick={changeFamily}
                    hide={schema?.idfamilia?.isHidden}
                />
                <ProductInfoRow
                    label="label_status"
                    value={
                        getBackendBoolean(data?.Available)
                            ? getLiteral('label_product_status_active')
                            : getLiteral('label_product_status_inactive')
                    }
                    hide={schema?.nodisponible?.isHidden}
                />
                <ProductInfoRow
                    label="label_environment"
                    value={environmentsText}
                    hide={schema?.strIdEnvironments?.isHidden || !environmentsText}
                />
                <ProductInfoRow
                    label="label_created_date"
                    value={getLocalDate(data.Created, 'DD/MM/YYYY HH:mm:ss', 'L LT')}
                />
            </div>
        </div>
    );
});

export default ProductInfo;
